import { useCurrentUser } from '@artic/api-client';
import { AppNav, type NavGroup } from '@artic/ui/app-nav';
import { Badge } from '@artic/ui/badge';
import { useFeatureFlag } from '@artic/ui/feature-flags';
import {
  AdminIcon,
  CatalogueIcon,
  DashboardsIcon,
  FactoryIcon,
  LegacyIcon,
  OperatingCostsIcon,
  PlanningIcon,
  QualityIcon,
  SettingsIcon,
} from '@artic/ui/icons/nav';
import {
  getDashboardsUrl,
  getDrawingsUrl,
  getHubUrl,
  getStorybookUrl,
} from '@artic/ui/lib/environment';
import { Logo } from '@artic/ui/logo';
import { LogoMark } from '@artic/ui/logo-mark';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@artic/ui/sidebar';
import { Link, useRouterState } from '@tanstack/react-router';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { usePartnerName } from '../features/inbox/use-partner-name';
import { NavUser } from './nav-user';
import { RouterNavLink } from './router-nav-link';

/**
 * Builds the translated navigation groups available to the current user.
 *
 * Navigation entries are configured according to feature flags, environment URLs,
 * administrator access, and legacy-grid availability.
 *
 * @returns The navigation groups for the current user
 */
function useNavItems(): NavGroup[] {
  const { t } = useTranslation('common');
  const { user } = useCurrentUser();
  const isAdmin = user.email.endsWith('@artic.works') || user.email.endsWith('@trunk.works');
  // Hidden rather than faded when off, matching how the Hub has always gated
  // its own `use_legacy_grid` entries: a partner off the legacy grid should not
  // be pointed back at the legacy pages at all.
  const useLegacyGrid = useFeatureFlag('use_legacy_grid');
  const drawingControlUrl = React.useMemo(() => getDrawingsUrl(), []);
  const dashboardsUrl = React.useMemo(() => getDashboardsUrl(), []);
  const storybookUrl = React.useMemo(() => getStorybookUrl(), []);

  return React.useMemo(() => {
    const groups: NavGroup[] = [
      {
        // ENG-866. Each dashboard is gated by its own flag; both are seeded
        // admin_only, so a partner user sees the entry disabled until it is
        // promoted for them. The Dashboards app is the flagged target itself,
        // so there is no fallback page to fade towards.
        title: t('terms.dashboards'),
        icon: DashboardsIcon,
        defaultExpanded: true,
        items: [
          {
            title: t('terms.quality'),
            externalUrl: `${dashboardsUrl}/quality`,
            flag: 'quality_dashboard',
          },
          {
            title: t('terms.downtime'),
            externalUrl: `${dashboardsUrl}/downtime`,
            flag: 'downtime_dashboard',
          },
        ],
      },
      {
        title: t('terms.factory'),
        icon: FactoryIcon,
        defaultExpanded: true,
        items: [
          { title: t('terms.statusboard'), hubPath: '/statusboard' },
          { title: t('terms.flow'), hubPath: '/schedules/progress' },
          // ENG-775: Job Cards now live in Neo. Ships unflagged — Hub Legacy's
          // own nav item and deep links forward here.
          { title: t('terms.jobCards'), to: '/job-cards' },
        ],
      },
      {
        title: t('terms.quality'),
        icon: QualityIcon,
        defaultExpanded: true,
        items: [
          { title: t('terms.inbox'), to: '/inbox', flag: 'quality_inbox' },
          {
            title: t('terms.packs'),
            to: '/quality-packs',
            flag: 'quality_packs',
          },
          { title: t('terms.visionQualityChecks'), hubPath: '/quality/vision-quality-checks' },
          {
            title: t('terms.visionTestPlans'),
            to: '/vision_test_plans',
          },
        ],
      },
      {
        title: t('terms.planner'),
        icon: PlanningIcon,
        defaultExpanded: true,
        items: [
          { title: t('terms.projects'), to: '/projects', flag: 'planner' },
          { title: t('terms.production'), to: '/production-planner', flag: 'planner' },
          {
            title: t('terms.changeControl'),
            to: '/change_control',
            flag: 'change_control',
          },
          { title: t('terms.workforce'), hubPath: '/workforce' },
          {
            title: t('terms.drawingControl'),
            externalUrl: drawingControlUrl,
            flag: 'drawing_control',
          },
        ],
      },
      {
        title: t('terms.catalogue'),
        icon: CatalogueIcon,
        defaultExpanded: true,
        items: [
          { title: t('terms.products'), to: '/products', flag: 'planner' },
          { title: t('terms.operations'), to: '/operations', flag: 'planner' },
          {
            title: t('terms.runSheets'),
            to: '/run_sheets',
            exact: true,
            flag: 'planner',
          },
        ],
      },
      // ENG-871: deliberately its own section, not Settings — baselines change
      // as the plan changes. The Cost to Produce reports view joins when its
      // spec lands.
      {
        title: t('terms.operatingCosts'),
        icon: OperatingCostsIcon,
        defaultExpanded: true,
        items: [
          {
            title: t('terms.overheads'),
            to: '/operating-costs/overheads',
            flag: 'operating_costs',
          },
          {
            title: t('terms.support'),
            to: '/operating-costs/support',
            flag: 'operating_costs',
          },
          {
            title: t('terms.valueAdding'),
            to: '/operating-costs/value-adding',
            flag: 'operating_costs',
          },
        ],
      },
      {
        title: t('terms.settings'),
        icon: SettingsIcon,
        items: [
          {
            kind: 'subheader',
            label: t('terms.factory'),
            items: [
              {
                title: t('terms.factoryAdmin'),
                to: '/factories',
                flag: 'settings_factory',
              },
              {
                title: t('terms.causeCategories'),
                to: '/cause_categories',
                flag: 'causes',
              },
              {
                title: t('terms.routes'),
                to: '/factory-routes',
                flag: 'routing',
              },
              {
                title: t('terms.stationsOperationsGrid'),
                to: '/operations-stations-grid',
                flag: 'settings_factory',
              },
            ],
          },
          {
            kind: 'subheader',
            label: t('terms.columns'),
            items: [
              {
                title: t('terms.containers'),
                to: '/products/containers',
                flag: 'planner',
              },
              {
                title: t('terms.columns'),
                to: '/columns/structured-configuration',
                flag: 'planner',
              },
              {
                title: t('terms.customColumns'),
                to: '/columns/custom-configuration',
                flag: 'planner',
              },
            ],
          },
          {
            kind: 'subheader',
            label: t('terms.imports'),
            items: [
              {
                title: t('terms.imports'),
                to: '/imports',
                flag: 'imports',
              },
            ],
          },
          {
            kind: 'subheader',
            label: t('terms.workforce'),
            items: [
              {
                title: t('terms.shiftPatterns'),
                to: '/shift-patterns',
                flag: 'capacity_plan',
              },
            ],
          },
        ],
      },
      {
        title: t('terms.articAdmin'),
        icon: AdminIcon,
        hide: !isAdmin,
        items: [
          // Always on for admins (ENG-628) — the partner-facing counterpart
          // lives under Settings > Columns behind the planner flag.
          {
            title: t('terms.containerLibrary'),
            to: '/admin/containers',
          },
          {
            title: t('terms.auditViewer'),
            to: '/audit',
            exact: true,
          },
          { title: t('terms.featureFlags'), to: '/admin/feature-flags' },
          { title: t('terms.partners'), to: '/admin/partners' },
          { title: t('terms.devices'), to: '/admin/devices' },
          {
            kind: 'subheader',
            label: t('terms.resources'),
            items: [
              storybookUrl
                ? { title: t('terms.storybook'), externalUrl: storybookUrl }
                : { title: t('terms.storybook'), disabled: true },
            ],
          },
        ],
      },
      {
        title: t('terms.legacy'),
        icon: LegacyIcon,
        hide: !isAdmin,
        items: [
          { title: t('terms.liveFeedQuality'), hubPath: '/feed' },
          { title: t('terms.gridSchedule'), hubPath: '/schedules/overview' },
          { title: t('terms.profiles'), hubPath: '/profiles' },
          {
            title: t('terms.drawingsLegacy'),
            hubPath: '/drawings',
            hide: !useLegacyGrid,
          },
        ],
      },
    ];

    return groups;
  }, [isAdmin, useLegacyGrid, dashboardsUrl, drawingControlUrl, storybookUrl, t]);
}

export function QualitySidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation('common');
  const hubUrl = React.useMemo(() => getHubUrl(), []);
  const { state } = useSidebar();
  const partnerName = usePartnerName();
  const navItems = useNavItems();
  const currentPath = useRouterState({
    select: (state) => state.location.pathname,
  });

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              tooltip={t('terms.quality')}
              className="h-auto justify-center py-3"
            >
              <Link to="/">
                {state === 'collapsed' ? (
                  <LogoMark size="sm" />
                ) : (
                  <div className="flex w-full min-w-0 flex-col items-center gap-2">
                    <Logo className="h-8! w-auto!" />
                    <Badge
                      variant="secondary"
                      className="border-border text-muted-foreground max-w-full px-3"
                    >
                      <span className="truncate">{partnerName ?? 'Artic'}</span>
                    </Badge>
                  </div>
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <AppNav
          groups={navItems}
          LinkComponent={RouterNavLink}
          currentPath={currentPath}
          hubUrl={hubUrl}
          label={t('navigation.label')}
          unavailableLabel={t('navigation.notAvailableForYourAccount')}
        />
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
        <p className="text-muted-foreground text-center text-[10px] select-all">{__COMMIT_SHA__}</p>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
